import { apiFetch } from "../apiFetch";

export type StudioTextPreviewChannel = "chat" | "voice" | "outbound_voice";

export type StudioTextPreviewResult = {
  matched: boolean;
  key?: string;
  intent?: string;
  reply?: string;
  voiceLines?: string[];
  escalateToStaff?: boolean;
  prohibited?: boolean;
  source?: string;
  version?: string;
};

export type StudioVoicePreviewLane = {
  checkedAt?: string;
  lane: "internal_preview_only";
  provider: "elevenlabs";
  mode: "preview_only";
  enabled: boolean;
  ready: boolean;
  status:
    | "preview_only_disabled"
    | "preview_only_blocked"
    | "preview_only_ready";
  executionPath: "/api/internal/voice/preview/render";
  allowedChannels: string[];
  blockedLaunchChannels: string[];
  blockedReasons: string[];
  personaSelection: {
    personaKey: string;
    personaName: string;
    styleVersion: string;
    voiceIdSource:
      | "elevenlabs_preview_voice_id"
      | "elevenlabs_voice_id"
      | "missing";
    voiceIdConfigured: boolean;
    modelIdConfigured: boolean;
    selectedStudioPersonaKey?: string | null;
    bindingResolution?: "studio_catalog" | "server_env_default";
    fallbackReason?: "studio_persona_unmapped" | null;
    catalogEntryKey?: string | null;
    catalogEntryLabel?: string | null;
    activeVoiceLabel?: string;
  };
  isolation: {
    previewOnly: true;
    launchCriticalChannelBlocked: true;
    twilioProductionRoutingUntouched: true;
  };
};

export type StudioLaunchControlState = {
  checkedAt?: string;
  overallReady: boolean;
  blockerCount: number;
  blockerCategories?: string[];
  nextActions?: Array<{
    category?: string;
    action?: string;
  }>;
};

export type StudioPreviewAccessState = {
  status: "ready" | "blocked";
  reason: "auth_required" | null;
};

export type StudioPreviewCenterState = {
  access: StudioPreviewAccessState;
  textPreview: {
    supportedChannels: StudioTextPreviewChannel[];
    mode: "internal_only";
  };
  voiceReadiness: StudioVoicePreviewLane | null;
  deploymentTruth: StudioLaunchControlState | null;
  errors: {
    voiceReadiness: string | null;
    deploymentTruth: string | null;
  };
};

export type StudioVoicePreviewHumanizationSummary = {
  version: string;
  channel: "voice";
  total: {
    score: number;
    maxScore: number;
    status: "pass" | "warn" | "fail";
  };
  dimensions: {
    repetition: number;
    concision: number;
    warmth: number;
    professionalism: number;
    closeQuality: number;
    forbiddenDrift: number;
  };
  flags: {
    acknowledgmentPresent: boolean;
    politeClosePresent: boolean;
    clearClosePresent: boolean;
    forbiddenDriftPresent: boolean;
    repeatedSentenceCount: number;
    repeatedStartCount: number;
  };
};

export type StudioVoicePreviewExecutionResult = {
  ok: boolean;
  error?: string;
  preview?: {
    status: "rendered" | "blocked" | "provider_error";
    provider?: "elevenlabs";
    mode?: "preview_only";
    channel: string;
    correlationId: string | null;
    personaKey: string;
    personaSelection?: StudioVoicePreviewLane["personaSelection"];
    textLength: number;
    requestId?: string | null;
    blockedReasons?: string[];
    requestContract?: {
      method: "POST";
      endpointTemplate: "/v1/text-to-speech/{voice_id}";
    };
    audio?: {
      encoding: "base64";
      contentType: string;
      bytes: number;
      data?: string;
    };
    humanization: StudioVoicePreviewHumanizationSummary;
    readiness: {
      status: StudioVoicePreviewLane["status"];
      blockedReasons: string[];
    };
    isolation: StudioVoicePreviewLane["isolation"];
  };
};

function isUnauthorized(response: Response | null | undefined) {
  return response?.status === 401 || response?.status === 403;
}

async function safeJson<T>(response: Response | null | undefined, fallback: T) {
  if (!response) return fallback;

  try {
    return (await response.json()) as T;
  } catch {
    return fallback;
  }
}

export async function loadStudioPreviewCenterState(input?: {
  studioPersonaKey?: string | null;
}): Promise<StudioPreviewCenterState> {
  const voicePreviewPath = input?.studioPersonaKey
    ? `/api/internal/voice/preview?studioPersonaKey=${encodeURIComponent(
        input.studioPersonaKey
      )}`
    : "/api/internal/voice/preview";
  const [voiceLaneResponse, launchControlResponse] = await Promise.all([
    apiFetch(voicePreviewPath),
    apiFetch("/api/internal/readiness/launch-control"),
  ]);

  if (isUnauthorized(voiceLaneResponse) || isUnauthorized(launchControlResponse)) {
    return {
      access: {
        status: "blocked",
        reason: "auth_required",
      },
      textPreview: {
        supportedChannels: ["chat", "voice", "outbound_voice"],
        mode: "internal_only",
      },
      voiceReadiness: null,
      deploymentTruth: null,
      errors: {
        voiceReadiness: null,
        deploymentTruth: null,
      },
    };
  }

  let voiceReadiness: StudioVoicePreviewLane | null = null;
  let deploymentTruth: StudioLaunchControlState | null = null;
  let voiceReadinessError: string | null = null;
  let deploymentTruthError: string | null = null;

  if (!voiceLaneResponse?.ok) {
    voiceReadinessError = "Voice preview lane unavailable.";
  } else {
    const payload = await safeJson<{ ok?: boolean; previewLane?: StudioVoicePreviewLane }>(
      voiceLaneResponse,
      {}
    );
    voiceReadiness = payload.previewLane || null;
    if (!voiceReadiness) {
      voiceReadinessError = "Voice preview lane data malformed.";
    }
  }

  if (!launchControlResponse?.ok) {
    deploymentTruthError = "Launch control unavailable.";
  } else {
    const payload = await safeJson<StudioLaunchControlState>(
      launchControlResponse,
      {} as StudioLaunchControlState
    );
    if (
      typeof payload?.overallReady === "boolean" &&
      typeof payload?.blockerCount === "number"
    ) {
      deploymentTruth = payload;
    } else {
      deploymentTruthError = "Launch control data malformed.";
    }
  }

  return {
    access: {
      status: "ready",
      reason: null,
    },
    textPreview: {
      supportedChannels: ["chat", "voice", "outbound_voice"],
      mode: "internal_only",
    },
    voiceReadiness,
    deploymentTruth,
    errors: {
      voiceReadiness: voiceReadinessError,
      deploymentTruth: deploymentTruthError,
    },
  };
}

export async function runStudioTextPreview(input: {
  channel: StudioTextPreviewChannel;
  message: string;
}) {
  const response = await apiFetch("/api/internal/playbooks/preview", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      channel: input.channel,
      message: input.message,
    }),
  });

  return {
    response,
    payload: await safeJson<StudioTextPreviewResult | { error?: string }>(response, {
      matched: false,
    }),
  };
}

export async function runStudioVoicePreview(input: {
  channel: string;
  text: string;
  correlationId?: string | null;
  studioPersonaKey?: string | null;
}) {
  const response = await apiFetch("/api/internal/voice/preview/render", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      channel: input.channel,
      text: input.text,
      correlationId: input.correlationId,
      studioPersonaKey: input.studioPersonaKey,
    }),
  });

  return {
    response,
    payload: await safeJson<StudioVoicePreviewExecutionResult | { error?: string }>(
      response,
      {
        ok: false,
        error: "voice_preview_failed",
      }
    ),
  };
}
